import React from "react";
import { CheckIcon, LockClosedIcon } from "@heroicons/react/outline";
import { cn } from "./cn";

/**
 * Bun stepper (onboarding). Spec: "People, progress & loading".
 * Done = ink circle + check; current = blue-ringed number + strong label;
 * upcoming = hairline circle + faint label. Connectors darken as you pass.
 *
 *   <Stepper steps={[{ id, label }, ...]} current={2} />
 */
export default function Stepper({ steps, current = 0, completed = current, onStepClick, variant = "default", mobileBars = false, ariaLabel, className }) {
  if (variant === 'segments') {
    return <ol aria-label={ariaLabel} className={cn('m-0 flex list-none gap-1 p-0', className)}>
      {steps.map((step, index) => <li key={step.id ?? index}
        className={cn('h-[5px] min-w-0 flex-1 rounded-full', step.state === 'complete' ? 'bg-bun-blue' : 'bg-bun-line')}>
        <span className="sr-only">{step.label}: {step.stateLabel || step.state}</span>
      </li>)}
    </ol>;
  }
  if (variant === 'inline') {
    return (
      <ol aria-label={ariaLabel} className={cn('m-0 flex list-none items-center gap-1 p-0 font-instrument sm:gap-[6px]', className)}>
        {steps.map((step, index) => {
          const done = index < current;
          const active = index === current;
          return <li key={step.id ?? index} aria-current={active ? 'step' : undefined}
            aria-label={`${step.label} step${done ? ', completed' : active ? ', current' : ''}`}
            className={cn('flex items-center gap-[6px]', mobileBars && 'flex-1 sm:flex-none')}>
            {mobileBars && <span aria-hidden="true" className={cn('h-[3px] w-full rounded-full sm:hidden', done || active ? 'bg-bun-ink' : 'bg-bun-line')} />}
            {index > 0 && <span aria-hidden="true" className={cn('h-px w-[22px]', mobileBars && 'hidden sm:block', done ? 'bg-bun-ink' : 'bg-bun-line-soft')} />}
            <span aria-hidden="true" className={cn(
              'h-6 w-6 shrink-0 items-center justify-center rounded-full text-bun-label font-semibold', mobileBars ? 'hidden sm:inline-flex' : 'inline-flex',
              done || active ? 'bg-bun-ink text-bun-on-ink' : 'bg-bun-mist text-bun-faint', active && 'ring-[3px] ring-bun-ring'
            )}>
              {done ? <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M5 13l4 4L19 7" /></svg> : index + 1}
            </span>
            <span className={cn('whitespace-nowrap text-bun-meta font-strong', mobileBars && 'sr-only sm:not-sr-only', active ? 'text-bun-text' : done ? 'text-bun-slate' : 'text-bun-faint')}>{step.label}</span>
          </li>;
        })}
      </ol>
    );
  }
  if (variant === "dots") {
    return (
      <span role="list" aria-label={ariaLabel} className={cn("inline-flex flex-none flex-nowrap items-center gap-1", className)}>
        {steps.map((step, index) => {
          const state = step.state || (index < current ? "complete" : index === current ? "current" : "upcoming");
          return (
            <span
              key={step.id ?? index}
              role="listitem"
              className={cn(
                "inline-flex h-[7px] w-[7px] flex-none items-center justify-center rounded-full",
                state === "complete" ? "bg-bun-blue" : "bg-bun-track-off"
              )}
            >
              <span className="sr-only">{step.label}: {step.stateLabel || state}</span>
            </span>
          );
        })}
      </span>
    );
  }
  if (variant === "vertical") {
    return (
      <ol aria-label={ariaLabel} className={cn("grid list-none space-y-3 font-instrument", className)}>
        {steps.map((step, index) => {
          const state = step.state || (index < current ? "complete" : index === current ? "current" : "upcoming");
          return (
            <li key={step.id ?? index} aria-current={state === "current" ? "step" : undefined}
              className={cn("relative grid grid-cols-[1.35rem_minmax(0,1fr)] gap-2", state === "current" ? "-mx-3 px-3 py-3" : "pb-2.5 last:pb-0")}>
              {state === "current" && <span data-current-step-background className="pointer-events-none absolute inset-y-0 left-1.5 right-3 rounded-bun-sm bg-bun-tint-blue" aria-hidden="true" />}
              {index < steps.length - 1 && <span aria-hidden="true" className={cn("absolute -bottom-3 w-px bg-bun-line-soft", state === "current" ? "left-[1.39rem] top-[2.1rem]" : "left-[0.64rem] top-[1.35rem]")} />}
              <span aria-hidden="true" className={cn("relative z-10 inline-flex h-[1.35rem] w-[1.35rem] items-center justify-center rounded-full border text-bun-label font-strong tracking-normal",
                state === "current" ? "border-bun-blue bg-bun-surface text-bun-blue" : state === "complete" ? "border-bun-blue bg-bun-blue text-bun-on-ink" : "border-bun-line bg-bun-surface text-bun-stone")}>
                {state === "complete" ? <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M5 13l4 4L19 7" /></svg> : index + 1}
              </span>
              <div className="relative z-10 grid min-w-0 gap-0.5">
                <span className={cn("text-bun-label font-semibold uppercase tracking-[0.07em]", state === "current" ? "text-bun-blue" : "text-bun-stone")}>
                  {step.stateLabel || (state === "complete" ? "Completed" : state === "current" ? "Current step" : "Upcoming")}
                </span>
                <strong className="text-bun-meta font-strong leading-[1.35] text-bun-text">{step.label}</strong>
                {step.meta && <span className="text-bun-label font-book leading-[1.4] text-bun-stone">{step.meta}</span>}
              </div>
            </li>
          );
        })}
      </ol>
    );
  }
  return (
    <div className={cn("flex items-center font-instrument", className)}>
      {steps.map((step, i) => {
        const done = i < completed;
        const active = i === current;
        const Step = onStepClick ? 'button' : 'div';
        return (
          <React.Fragment key={step.id ?? i}>
            {i > 0 && (
              <span
                className={cn(
                  "flex-1 h-[1.5px] mx-1 mb-[18px]",
                  i <= current ? "bg-bun-ink" : "bg-bun-line"
                )}
              />
            )}
            <Step
              className={cn('flex min-w-0 flex-col items-center gap-[5px]', onStepClick && 'min-h-11 flex-1 rounded-bun px-1 py-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-bun-blue disabled:cursor-default')}
              aria-current={active ? 'step' : undefined}
              {...(onStepClick ? { type: 'button', disabled: i > completed, onClick: () => onStepClick(i) } : {})}
            >
              <span
                className={cn(
                  "w-[22px] h-[22px] rounded-full inline-flex items-center justify-center box-border text-[10px] font-semibold",
                  done && "bg-bun-ink",
                  active && onStepClick && "ring-[3px] ring-bun-ring",
                  active && !done && "bg-bun-surface border-2 border-bun-blue text-bun-blue",
                  !done && !active && "bg-bun-surface border-[1.5px] border-bun-line text-bun-faint font-strong"
                )}
              >
                {done ? (
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--bun-on-ink)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  i + 1
                )}
              </span>
              <span
                className={cn(
                  onStepClick ? 'text-bun-meta text-center leading-snug' : 'text-[10px] whitespace-nowrap',
                  active ? "font-strong text-bun-text" : done ? "font-book text-bun-stone" : "font-book text-bun-faint"
                )}
              >
                {step.label}
              </span>
            </Step>
          </React.Fragment>
        );
      })}
    </div>
  );
}

/** Passive step marker for task rows; the enclosing control supplies its label. */
Stepper.Marker = function StepMarker({ number, state = 'upcoming' }) {
  return <span aria-hidden="true" className={cn(
    'inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border text-bun-meta font-strong',
    state === 'complete' ? 'border-bun-success-bg bg-bun-success-bg text-bun-success'
      : state === 'locked' ? 'border-dashed border-bun-line-strong text-bun-faint'
        : state === 'current' ? 'border-bun-ink text-bun-text' : 'border-bun-line-strong text-bun-stone'
  )}>
    {state === 'complete' ? <CheckIcon className="h-3 w-3" /> : state === 'locked' ? <LockClosedIcon className="h-3 w-3" /> : number}
  </span>;
};
